import { useState } from 'react';

import { ArrowButton, Button, DatePicker, Modal, TextField, Typography } from "@king-ict/eupisi-ui/components";
import { useValidation } from '@king-ict/eupisi-ui/hooks';
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router";

import { createLanguage, editLanguage } from "../../api/nisooApi";
import { useInput } from "../../hooks";
import { CodeBookExtended, CodeBookValidationScheme } from '../../models/CodeBook';
import { codebookRouteLanguage } from '../../routes/routes';
import styles from "./RequestCodeBook.module.scss";

export default function CodeBookLanguageEdit(): JSX.Element {
  const history = useHistory();
  const location = useLocation();
  const { handleChange } = useInput();
  const { t } = useTranslation();

  const getLanguageInitial = () => {
    if ((location.state as any).language) {
      return (location.state as any).language as CodeBookExtended;
    }
    return { id: 0, name: "", canDelete: true };
  }

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [language, setLanguage] = useState<CodeBookExtended>(getLanguageInitial());

  const [errors, doValidations] = useValidation({
    data: language,
    validationSchema: CodeBookValidationScheme,
  });

  const saveChanges = () => {
    var isValid = doValidations();
    if (isValid) {
      setOpenConfirmModal(true);
    }
  }

  const handleCancelConfirmModal = async () => {
    setOpenConfirmModal(false);
  }

  const handleConfirmedConfirmModal = async () => {
    setOpenConfirmModal(false);
    if (language.dateTo) {
      let date = new Date(language.dateTo);
      language.dateTo = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    }

    if (language.id) {
      await editLanguage(language.id, language);
      navigateToDetails();
    }
    else {
      await createLanguage(language);
      navigateToDetails();
    }
  }

  const cancel = () => {
    if (language.id) {
      navigateToDetails()
    } else {
      navigateToList();
    }
  }

  const navigateToDetails = () => {
    history.push(
      codebookRouteLanguage(),
      {
        page: "details",
        language: language
      }
    );
  }

  const navigateToList = () => {
    history.push(
      codebookRouteLanguage()
    );
  }
  return (
    <>
      <Row>
        <Col md={12}>
          <ArrowButton
            backwards
            ariaLabel=""
            onClick={navigateToList}
          />
          <span className={styles.backButtonCaption}>
            {t("codeBook.navigateToCodeBook")}
          </span>

        </Col>
      </Row>
      <Row className={styles.title}>
        <Col md={6}>
          <h3>{t("codeBook.language")}</h3>
        </Col>
        <Col md={6} className="d-flex justify-content-end align-items-right">
          <Button
            outline
            onClick={() => cancel()}
            className={styles.actionButton}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => saveChanges()}
            className={styles.actionButton}>
            {t("common.save")}
          </Button>
        </Col>
      </Row>
      <Row>
        <Col md={8} className="mt-5">
          <TextField
            readonly
            name={nameof<typeof language>("name")}
            label={t("common.name")}
            value={language.name!}
            onChange={handleChange(setLanguage)}
            errorText={errors?.name}
          />
        </Col>
      </Row>
      <Row>
        <Col md={4} className="mt-5">
          <DatePicker
            name={nameof<typeof language>("dateTo")}
            label={t("common.dateTo")}
            value={language.dateTo}
            onChange={handleChange(setLanguage)}
            isClearable={true}
            minYear={new Date().getFullYear() - 25}
            maxYear={new Date().getFullYear() + 25}
          />
        </Col>
      </Row>
      <Modal
        type="confirm"
        open={openConfirmModal}
        cancelText={t("common.cancel")}
        confirmText={t("common.saveChanges")}
        onClose={handleCancelConfirmModal}
        onConfirm={handleConfirmedConfirmModal}
      >
        <div>
          <Typography component="h1" variant="largeBold">
            {t("common.areYouSureSaveChanges")}
          </Typography>
        </div>
      </Modal>
    </>
  );
}